import TextArea from "../../../Components/Common/TextArea/TextArea";
import { CHAR_LIMIT } from "../../../utils/Common/Constant";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useBankLayout } from "../BankLayout";
import CustomModal from "../../../Components/Common/CustomModal/CustomModal";
import DrawSection from "../BankDetails/DrawSection";
import OverviewIcon from "../../../Assets/Img/OverviewIcon";
import Sidebar from "../../../Components/Sidebar/Sidebar";
import DocIcon from "../../../Assets/Img/DocIcon";
import ProgressIcon from "../../../Assets/Img/ProgressIcon";
import LineWaiverIcon from "../../../Assets/Img/LineWaiverIcon";
import HistoryIcon from "../../../Assets/Img/HistoryIcon";
import DrawRequestIcon from "../../../Assets/Img/DrawRequestIcon";
import MessageIcon from "../../../Assets/Img/MessageIcon";
import ProfileIcon from "../../../Assets/Img/ProfileIcon";
import Button from "Components/Common/Button/Button";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import ScrollToTop from "Components/Common/ScrollToTop/ScrollToTop";
import ConversationPanel from "Components/Common/ConversationPanel/ConversationPanel";
import { useToast } from "Components/Common/Toast/ToastContext";
import { post } from "utils/api/apiCaller";
import { ENDPOINTS } from "utils/api/endpoints";
import { getLenderProjectDetail } from "services/lenderService";
import { getLenderProjectOverview } from "services/projectService";
import { getLenderSovVersions } from "services/lenderService";
import SOVSetup from "Pages/GcPrecon/GcProjectDetails/components/sections/SOVSetup";
import { formatDate } from "utils/Common/Date";
import { type LenderOverviewData } from "../BankDetails/DrawSection";
import { type Project } from "hooks/useGetProjects";
import { ProjectDetail, getCardVariant } from "./BankDashboard";
import ChangeOrderIcon from "Assets/Img/ChangeOrderIcon";
import RequirementsAcknowledgedSection from "Pages/Admin/ProjectDetail/sections/RequirementsAcknowledgedSection";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import NoDocuments from "Assets/Img/NoDocuments";
import PreconChecklistIcon from "Assets/Img/PreconChecklistIcon";
import RequirementsAcknowledgedIcon from "Assets/Img/RequirementsAcknowledgedIcon";
import ScheduleOfValuesIcon from "Assets/Img/ScheduleOfValuesIcon";
import PreConstructionReportIcon from "Assets/Img/PreConstructionReportIcon";

interface SovVersion {
  id: string;
  versionNumber: number;
  status: string;
}

type ModalType = "approve" | "reject" | "comment";

export default function BankProjectDetail() {
  const { projectId, activeTab } = useParams<{
    projectId: string;
    activeTab?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();

  const project: Project | null =
    (location.state as { project?: Project })?.project ?? null;

  const { setCrumbs } = useBankLayout();

  const [activeSection, setActiveSection] = useState(activeTab ?? "overview");
  const [expandedDraw, setExpandedDraw] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<ModalType | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(
    null,
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [overviewData, setOverviewData] = useState<LenderOverviewData | null>(
    null,
  );
  const [sovVersions, setSovVersions] = useState<SovVersion[]>([]);
  const [sovVersionsLoaded, setSovVersionsLoaded] = useState(false);

  useEffect(() => {
    setCrumbs([
      { label: "My Projects", link: "/bank-dashboard" },
      { label: projectDetail?.name ?? project?.name ?? "Project Detail" },
    ]);
  }, [projectDetail?.name, project?.name]);
  const [approveNote, setApproveNote] = useState("");
  const [rejectNote, setRejectNote] = useState("");

  const fetchProjectDetail = () => {
    if (!projectId) return;
    setDetailLoading(true);
    (getLenderProjectDetail(projectId) as Promise<any>).then((res: any) => {
      if (!res.error && res.data?.data) setProjectDetail(res.data.data);
      setDetailLoading(false);
    });
  };

  useEffect(() => {
    fetchProjectDetail();
    if (!projectId) return;
    (getLenderProjectOverview(projectId) as Promise<any>).then((res: any) => {
      if (!res.error && res.data?.data) setOverviewData(res.data.data);
    });
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    getLenderSovVersions(projectId).then(
      ({ data }: { data: { data?: SovVersion[] } | null }) => {
        if (data && Array.isArray(data.data)) {
          setSovVersions(
            [...data.data].sort((a, b) => b.versionNumber - a.versionNumber),
          );
        }
        setSovVersionsLoaded(true);
      },
    );
  }, [projectId]);

  useEffect(() => {
    if (activeSection !== "sov-version" || sovVersions.length === 0) return;
    const firstId = sovVersions[0].id;
    setActiveSection(`sov-version-${firstId}`);
    navigate(`/bank-project-detail/${projectId}/sov-version-${firstId}`, {
      replace: true,
    });
  }, [activeSection, sovVersions]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    navigate(`/bank-project-detail/${projectId}/${section}`, { replace: true });
    window.scrollTo(0, 0);
  };

  const closeModal = () => setOpenModal(null);

  const handleApprove = async () => {
    if (!project?.id || !project?.lastDrawId) {
      closeModal();
      showToast("Draw information not available.");
      return;
    }
    setSubmitting(true);
    const { error } = (await post(
      ENDPOINTS.DRAWS.APPROVE(project.id, project.lastDrawId),
      { note: approveNote },
    )) as { error: { message?: string } | null };
    setSubmitting(false);
    closeModal();
    if (error) {
      showToast(error.message || "Failed to approve draw.");
    } else {
      showToast("Draw approved successfully.");
      fetchProjectDetail();
    }
  };

  const handleReject = async () => {
    if (!project?.id || !project?.lastDrawId) {
      closeModal();
      showToast("Draw information not available.");
      return;
    }
    setSubmitting(true);
    const { error } = (await post(
      ENDPOINTS.DRAWS.REJECT(project.id, project.lastDrawId),
      { note: rejectNote },
    )) as { error: { message?: string } | null };
    setSubmitting(false);
    closeModal();
    if (error) {
      showToast(error.message || "Failed to reject draw.");
    } else {
      showToast("Draw rejected.");
      fetchProjectDetail();
    }
  };

  const handleComment = () => {
    closeModal();
    showToast("Comment posted to project record.");
  };

  const drawLabel = project?.lastDrawNumber
    ? ` #${String(project.lastDrawNumber).padStart(2, "0")}`
    : "";

  const selectedVariant = projectDetail?.status
    ? getCardVariant({ status: projectDetail.status } as any)
    : null;
  const progress = parseFloat(projectDetail?.progressPercentage ?? "0");

  const bankSections = [
    {
      label: "Project",
      items: [
        { id: "overview", label: "Overview", icon: <OverviewIcon /> },
        {
          id: "aia",
          label: "AIA Summary ",
          icon: <DrawRequestIcon />,
          badge: { text: "View", variant: "view" },
        },
        { id: "draws", label: "Draw History", icon: <HistoryIcon /> },
        {
          id: "change-orders",
          label: "Change Orders",
          icon: <ChangeOrderIcon />,
        },
      ],
    },
    {
      label: "Reports",
      items: [
        {
          id: "reports",
          label: "Progress Reports",
          icon: <ProgressIcon />,
          badge: { text: "View", variant: "view" },
        },
        {
          id: "precon",
          label: "Pre-Construction",
          icon: <PreConstructionReportIcon />,
          badge: { text: "View", variant: "view" },
        },
      ],
    },
    {
      label: "Project Info",
      items: [
        {
          id: "contacts",
          label: "Contacts",
          icon: <ProfileIcon />,
          badge: { text: "View", variant: "view" },
        },
        {
          id: "documents",
          label: "Documents",
          icon: <DocIcon />,
          badge: { text: "View", variant: "view" },
        },
        {
          id: "requirements-acknowledged",
          label: "Requirements Acknowledged",
          icon: <RequirementsAcknowledgedIcon />,
        },
        {
          id: "precon-checklist",
          label: "Pre-Con Checklist",
          icon: <PreconChecklistIcon />,
        },
      ],
    },
    {
      label: "Review SOV",
      items: [
        {
          id: "sov-version",
          label: "Schedule of Values",
          icon: <ScheduleOfValuesIcon />,
          subItems: sovVersions.map((v) => ({
            id: `sov-version-${v.id}`,
            versionNumber: v.versionNumber,
            label: `Version ${v.versionNumber}`,
            badge: {
              text: v.status === "superseded" ? "Inactive" : v.status,
              variant: v.status === "active" ? "view" : "",
            },
          })),
        },
      ],
    },
    {
      label: "Support",
      items: [
        { id: "contact-csc", label: "Contact CSC", icon: <MessageIcon /> },
      ],
    },
    {
      label: "Communication",
      items: [{ id: "messages", label: "Messages", icon: <MessageIcon /> }],
    },
  ];

  return (
    <div className="bankDashboardSec">
      <ScrollToTop />
      <div className="screen active">
        <div className="body-wrap">
          <Sidebar
            headerContent={
              <Button
                className="btn btnSm"
                onClick={() => navigate("/bank-dashboard")}
              >
                ← <span> All Projects</span>
              </Button>
            }
            sections={bankSections}
            activeSection={activeSection}
            onSelect={handleSectionChange}
          />

          <div className="main">
            <div className="proj-banner">
              <div className="pb-icon">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <rect
                    x="2"
                    y="6"
                    width="18"
                    height="14"
                    rx="1"
                    stroke="#14508c"
                    strokeWidth="1.3"
                    fill="none"
                  />
                  <path
                    d="M5 6V4a1 1 0 011-1h10a1 1 0 011 1v2"
                    stroke="#14508c"
                    strokeWidth="1.3"
                    fill="none"
                  />
                </svg>
              </div>
              <div className="pbFlex">
                <div className="pb-name">
                  {detailLoading ? (
                    <Skeleton width={200} height={16} />
                  ) : (
                    (projectDetail?.name ?? "Project Detail")
                  )}
                </div>
                <div className="pb-meta">
                  {detailLoading ? (
                    <Skeleton width={220} height={12} />
                  ) : (
                    <>
                      <div className="pb-mi">
                        {projectDetail?.type ?? "—"} ·{" "}
                      </div>
                      <div className="pb-mi">
                        {projectDetail?.stakeholders?.gc?.companyName ?? "—"}
                      </div>
                      {overviewData?.report?.reportNumber != null && (
                        <div className="pb-mi">
                          · Report #
                          {String(overviewData.report.reportNumber).padStart(
                            2,
                            "0",
                          )}
                          {overviewData.report.reportDate
                            ? ` · ${formatDate(overviewData.report.reportDate, false)}`
                            : ""}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="pb-right">
                {detailLoading ? (
                  <>
                    <Skeleton width={90} height={24} borderRadius={12} />
                    <Skeleton
                      width={130}
                      height={16}
                      style={{ marginTop: 8 }}
                    />
                  </>
                ) : (
                  <>
                    {selectedVariant && (
                      <span className={`badge ${selectedVariant.badgeClass}`}>
                        <span className="bdot"></span>
                        {selectedVariant.badgeText}
                      </span>
                    )}
                    <div className="pcBarWrap">
                      <span className="pcBarPctBold">
                        {progress.toFixed(0)}%
                      </span>
                      <div className="pt">
                        <div
                          className="pt-fill"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {activeSection === "sov-version" &&
              sovVersionsLoaded &&
              sovVersions.length === 0 && (
                <div className="ds active">
                  <EmptyState
                    icon={<NoDocuments />}
                    title="Schedule of Values Not Submitted"
                    subtitle="The GC has not yet submitted a Schedule of Values for this project."
                  />
                </div>
              )}
            <SOVSetup
              isActive={activeSection.startsWith("sov-version-")}
              projectId={projectId}
              versionId={
                activeSection.startsWith("sov-version-")
                  ? activeSection.replace("sov-version-", "")
                  : undefined
              }
            />
            {activeSection === "requirements-acknowledged" && (
              <RequirementsAcknowledgedSection projectId={projectId ?? ""} />
            )}
            <ConversationPanel
              projectId={projectId ?? ""}
              projectName={project?.name}
              isActive={activeSection === "messages"}
              lender={projectDetail?.stakeholders?.lenderBranch}
            />

            {detailLoading ? (
              <div className="ds active">
                <div className="dsHeader">
                  <Skeleton width="30%" height={18} />
                  <Skeleton width={130} height={34} borderRadius={6} />
                </div>

                <div className="draw-action mt-3">
                  <div className="da-header">
                    <div>
                      <Skeleton
                        width="35%"
                        height={15}
                        style={{ marginBottom: 6 }}
                      />
                      <Skeleton width="55%" height={11} />
                    </div>
                  </div>
                  <div className="da-grid">
                    {[1, 2, 3].map((n) => (
                      <div className="da-item" key={n}>
                        <Skeleton
                          width="60%"
                          height={11}
                          style={{ marginBottom: 6 }}
                        />
                        <Skeleton width="40%" height={18} />
                      </div>
                    ))}
                  </div>
                  <div className="daBtnGroup">
                    <Skeleton width={130} height={36} borderRadius={6} />
                    <Skeleton width={120} height={36} borderRadius={6} />
                  </div>
                </div>

                <div className="draw-action">
                  <div className="da-header">
                    <div>
                      <Skeleton
                        width="45%"
                        height={15}
                        style={{ marginBottom: 6 }}
                      />
                      <Skeleton width="60%" height={11} />
                    </div>
                    <div className="daHeaderRight">
                      <Skeleton
                        width={90}
                        height={11}
                        style={{ marginBottom: 4 }}
                      />
                      <Skeleton width={110} height={22} />
                    </div>
                  </div>
                  <div className="da-grid">
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <div className="da-item" key={n}>
                        <Skeleton
                          width="60%"
                          height={11}
                          style={{ marginBottom: 6 }}
                        />
                        <Skeleton width="40%" height={18} />
                      </div>
                    ))}
                  </div>
                  <div className="daBtnGroup">
                    <Skeleton width={130} height={36} borderRadius={6} />
                    <Skeleton width={120} height={36} borderRadius={6} />
                    <Skeleton width={140} height={36} borderRadius={6} />
                    <Skeleton width={120} height={36} borderRadius={6} />
                  </div>
                </div>

                <div className="two-col">
                  <div className="card">
                    <Skeleton
                      width="40%"
                      height={14}
                      style={{ marginBottom: 12 }}
                    />
                    <Skeleton width="100%" height={90} />
                  </div>
                  <div className="card">
                    <Skeleton
                      width="40%"
                      height={14}
                      style={{ marginBottom: 12 }}
                    />
                    <Skeleton width="100%" height={90} />
                  </div>
                </div>

                <div className="card">
                  <Skeleton
                    width="30%"
                    height={14}
                    style={{ marginBottom: 12 }}
                  />
                  <Skeleton width="100%" height={60} />
                </div>
              </div>
            ) : (
              <DrawSection
                activeSection={activeSection}
                setOpenModal={setOpenModal}
                expandedDraw={expandedDraw}
                setExpandedDraw={setExpandedDraw}
                projectId={projectId}
                projectDetail={projectDetail}
                fetchProjectDetail={fetchProjectDetail}
                overviewData={overviewData}
              />
            )}
          </div>
        </div>
      </div>

      {/* MODALS */}
      <CustomModal
        show={openModal === "approve"}
        handleClose={closeModal}
        title={`Approve Draw${drawLabel}?`}
        isHeaderVisible={true}
        isCrossBtn={false}
        isFooterVisible={true}
        footerContent={
          <>
            <Button
              className="btn btn-secondary"
              onClick={closeModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-success"
              onClick={handleApprove}
              disabled={
                submitting || approveNote.length > CHAR_LIMIT.DESCRIPTION
              }
            >
              {submitting ? "Approving…" : "Confirm Approval"}
            </Button>
          </>
        }
      >
        Approving this draw for{" "}
        <strong>{projectDetail?.name ?? project?.name ?? "—"}</strong>. Add an
        optional note for the record:
        <TextArea
          className="form-control mt-3"
          placeholder="e.g. Approved pending GC clarification on electrical line item…"
          value={approveNote}
          onChange={(e) => setApproveNote(e.target.value)}
          maxChars={CHAR_LIMIT.DESCRIPTION}
        />
      </CustomModal>

      <CustomModal
        show={openModal === "reject"}
        handleClose={closeModal}
        title={`Reject Draw${drawLabel}?`}
        isHeaderVisible={true}
        isCrossBtn={false}
        isFooterVisible={true}
        footerContent={
          <>
            <Button
              className="btn btn-secondary"
              onClick={closeModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              className="btn reject-btn"
              onClick={handleReject}
              disabled={
                submitting || rejectNote.length > CHAR_LIMIT.DESCRIPTION
              }
            >
              {submitting ? "Rejecting…" : "Confirm Rejection"}
            </Button>
          </>
        }
      >
        Provide a reason for rejecting this draw. Your note will be shared with
        CSC New England and the GC.
        <TextArea
          className="form-control mt-3"
          placeholder="e.g. Holding pending resolution of electrical overbilling flag…"
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
          maxChars={CHAR_LIMIT.DESCRIPTION}
        />
      </CustomModal>

      <CustomModal
        show={openModal === "comment"}
        handleClose={closeModal}
        title="Add Comment"
        isHeaderVisible={true}
        isCrossBtn={false}
        isFooterVisible={true}
        footerContent={
          <>
            <Button className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button className="btn btn-primary" onClick={handleComment}>
              Post Comment
            </Button>
          </>
        }
      >
        <p className="modal-sub">
          Your comment will be visible to CSC New England and logged against
          this project.
        </p>
        <TextArea
          className="form-control mt-3 modal-sub"
          placeholder="Type your comment…"
          maxChars={CHAR_LIMIT.DESCRIPTION}
        />
      </CustomModal>
    </div>
  );
}
